// Starter roleplay scenarios. Pure frontend constants: picking one seeds the
// editor state (RoleplayEditorModal), where the author localizes names,
// dishes, and policies before saving. Criteria carry no ids — the backend
// mints durable criterion ids on first save, exactly like hand-added rows.
//
// Copy rules: no em dashes; write for hourly frontline employees.

export const ROLEPLAY_TEMPLATES = [
  {
    key: "cold_food",
    label: "Cold food complaint",
    emoji: "🍽️",
    name: "Cold Entrée Complaint",
    description:
      "A regular guest's entrée came out late and cold on a busy night. Practice acknowledging the problem, making it right, and knowing when to bring in a manager.",
    persona:
      "Dana, a regular guest who comes in every couple of weeks. Their entrée arrived 30 minutes after ordering and is now cold. Frustrated and disappointed but fundamentally reasonable: responds well to being heard and to concrete action, poorly to excuses.",
    situation:
      "A slammed dinner service. The kitchen fell behind and the trainee's table was forgotten during the rush. The trainee is the server responsible for the table. Policy: servers may comp a dish or offer a remake on their own authority, and should involve a manager beyond that or whenever the guest asks.",
    behavioral_directives:
      "Open frustrated but civil. Escalate noticeably if the first reply does not acknowledge the problem. Soften once the trainee genuinely apologizes and offers a concrete fix. Do not resolve until at least one concrete fix is on the table.",
    opening_line:
      "Excuse me. We ordered almost forty minutes ago, and this just got here completely cold. We come in all the time and it's never been like this.",
    criteria: [
      { label: "Apologize without making excuses", description: "Owns the miss sincerely without blaming the kitchen, the rush, or other staff." },
      { label: "Offer a concrete fix", description: "Proposes specific action: a fresh remake, a comp, or both. Vague sympathy does not count." },
      { label: "Escalate appropriately", description: "Brings in or offers a manager when the guest stays upset or asks, rather than over-promising." },
      { label: "Keep the relationship warm", description: "Acknowledges the guest as a valued regular and closes in a way that invites them back." },
    ],
    max_turns: 8,
    voice: "alloy",
  },
  {
    key: "allergen",
    label: "Allergen question",
    emoji: "⚠️",
    name: "Severe Allergy Question",
    description:
      "A parent asks whether a dish is safe for their child with a severe allergy. Practice taking allergen questions seriously and never guessing.",
    persona:
      "Sam, a parent ordering for their 8-year-old who has a severe peanut allergy. Calm but vigilant: they have had close calls at other restaurants and will press on any vague or uncertain answer.",
    situation:
      "A normal dinner service. The trainee is taking the table's order when the parent asks about allergens in a popular dish. Policy: never guess about allergens; check with the kitchen for every allergy question, and flag the allergy on the ticket so the kitchen prevents cross-contact.",
    behavioral_directives:
      "Stay calm but firm. Press harder if the trainee guesses or answers instantly without checking. Relax once the trainee commits to checking with the kitchen and flagging the ticket. Ask a follow-up about cross-contact before being satisfied.",
    opening_line:
      "Before we order anything, I need to ask: does the pad thai have peanuts, or touch peanuts at all in the kitchen? My son has a severe allergy.",
    criteria: [
      { label: "Take it seriously immediately", description: "Treats the allergy as safety-critical from the first reply, never casually." },
      { label: "Never guess", description: "Does not state ingredient facts from memory. Commits to verifying with the kitchen." },
      { label: "Address cross-contact", description: "Mentions flagging the ticket or kitchen precautions, not just the recipe's ingredients." },
      { label: "Give a safe path to order", description: "Closes with a clear, safe way forward the parent can trust." },
    ],
    max_turns: 6,
    voice: "alloy",
  },
  {
    key: "wrong_order",
    label: "Wrong order delivered",
    emoji: "🔄",
    name: "Wrong Order at the Table",
    description:
      "A guest received a different dish than they ordered and is now on a tight schedule. Practice fixing mistakes fast without getting defensive.",
    persona:
      "Priya, a guest on a lunch break with about 25 minutes left. Ordered one dish, received another. Not angry yet, but time-pressed and direct. Gets clipped if the fix sounds slow or if anyone argues about what was ordered.",
    situation:
      "Lunch rush. Ticket mix-up between two nearby tables. The trainee is the server. Policy: fix order errors immediately, prioritize a fast remake or a quick alternative, and comp when the guest's meal is materially disrupted.",
    behavioral_directives:
      "Lead with the time pressure. Escalate if the trainee debates whose mistake it was. Respond well to speed: a fast option, a realistic time estimate, or an offer to box it to go. Accept a reasonable fix quickly.",
    opening_line:
      "Sorry, this isn't what I ordered. I asked for the grilled chicken salad. I've got maybe twenty-five minutes before I need to be back at work.",
    criteria: [
      { label: "Fix first, explain later", description: "Moves straight to correcting the order without arguing about how it happened." },
      { label: "Respect the time constraint", description: "Offers a realistically fast option and an honest time estimate." },
      { label: "Make it right materially", description: "Offers a comp, a to-go box, or similar when the meal is disrupted." },
    ],
    max_turns: 6,
    voice: "marin",
  },
  {
    key: "slow_night",
    label: "Long wait, no updates",
    emoji: "⏳",
    name: "Long Wait With No Updates",
    description:
      "A party at the host stand has waited far past the quoted time without a single update. Practice owning the silence, giving an honest new estimate, and offering a goodwill gesture.",
    persona:
      "Marcus, waiting with his partner for a table 35 minutes past the quoted 20-minute wait. Nobody has spoken to them since check-in. More insulted by the silence than the wait itself.",
    situation:
      "A busy weekend night. Two large parties camped longer than expected and the wait list slipped. The trainee is at the host stand. Policy: give honest updated estimates, check in with waiting guests every 10 to 15 minutes, and offer a drink or appetizer gesture for long slips.",
    behavioral_directives:
      "Open irritated about the silence, not just the wait. Escalate if given another vague estimate. Soften with honesty, a specific new estimate, and any goodwill gesture. Decide to stay if handled well; threaten to leave if handled vaguely.",
    opening_line:
      "Hi. So we checked in almost an hour ago and were told twenty minutes. Nobody has said a word to us since. What's going on?",
    criteria: [
      { label: "Own the communication gap", description: "Apologizes specifically for the lack of updates, not just the wait." },
      { label: "Give an honest estimate", description: "Offers a specific, realistic new time rather than another vague reassurance." },
      { label: "Offer a goodwill gesture", description: "Extends a drink, appetizer, or similar within policy for the long slip." },
    ],
    max_turns: 6,
    voice: "cedar",
  },
  {
    key: "phone_complaint",
    label: "Angry phone call",
    emoji: "📞",
    name: "Angry Phone Call About a Pickup Order",
    description:
      "A caller got home and found their takeout order wrong and incomplete. Practice de-escalating without body language, voice only.",
    persona:
      "Jordan, calling 20 minutes after picking up a takeout order: one dish missing, another wrong. Already home, hungry family, not driving back tonight. Starts sharp and talks over vague apologies.",
    situation:
      "The trainee answers the phone during service. Policy: for takeout errors, offer a refund for missing or wrong items or a credit for a future visit; a manager can authorize a full-order refund. Delivery of a fix tonight is not possible.",
    behavioral_directives:
      "Start sharp and interrupt the first vague apology. Settle down when the trainee summarizes the problem back accurately and names a concrete remedy. Push once for something unreasonable (free meals for a month) and accept a policy-compliant fix if delivered confidently.",
    opening_line:
      "Yeah, hi. I just got home with our pickup order and half of it is wrong. We're missing the kids' pasta entirely and this is NOT what I ordered. What are you going to do about it?",
    criteria: [
      { label: "Listen and confirm the details", description: "Summarizes exactly what is missing and wrong before proposing anything." },
      { label: "Stay steady under sharpness", description: "Keeps a calm, non-defensive tone while the caller vents." },
      { label: "Offer a policy-compliant remedy", description: "Names the refund or credit options accurately without over-promising." },
      { label: "Hold a boundary gracefully", description: "Declines unreasonable demands politely while keeping the fix on the table." },
    ],
    max_turns: 8,
    voice: "alloy",
  },
  {
    key: "upsell",
    label: "Upsell practice",
    emoji: "✨",
    name: "Natural Upsell at the Table",
    description:
      "A friendly two-top is open to suggestions. Practice recommending add-ons that fit the guest instead of pushing.",
    persona:
      "Alex and Casey, a relaxed couple celebrating a small work win. Open to suggestions and happy to spend a bit more for something good, but allergic to pushy or scripted selling. They ask real questions about anything recommended.",
    situation:
      "A comfortable mid-week dinner. The trainee is the server taking drink and appetizer orders. The house priorities are a signature cocktail, a shareable appetizer, and dessert. The goal is recommendations that feel like hospitality, not a pitch.",
    behavioral_directives:
      "Be warm and chatty. Ask a follow-up question about anything recommended (what's in it, is it big enough to share). Go cold if the selling feels scripted or stacked. Say yes to at most two well-matched suggestions.",
    opening_line:
      "Hi! We're celebrating a little tonight, nothing fancy. What do you like here? We're pretty open.",
    criteria: [
      { label: "Read the moment", description: "Connects suggestions to the celebration instead of listing specials generically." },
      { label: "Recommend specifically", description: "Names actual items with a reason they fit, not a menu recitation." },
      { label: "Answer follow-ups knowledgeably", description: "Handles questions about the recommended items confidently." },
      { label: "Know when to stop", description: "Stops selling after the guests have said yes, keeping it hospitable." },
    ],
    max_turns: 8,
    voice: "coral",
  },
];

export default ROLEPLAY_TEMPLATES;
